import { useMutation } from '@apollo/client';
import { OUTBOUND, INBOUND } from 'constants/booking';
import { OVERRIDE_PRICE } from 'store/mutations';
import { isDirectFareLegs, isThroughFareLegs, isSummatedFareLegs, overridePriceRequest } from 'utils/summatedFaresOverridePrice';
import { overridePriceRequestSN } from 'utils/SNSummatedFares/SNSummatedOverridePrice';
import serviceTypeCodes from 'constants/serviceTypeCodes';

const useSummatedFaresOverrideMutation = () => {
  const [overridePrice, { loading: overridePriceLoading }] = useMutation(OVERRIDE_PRICE);

  const handleMutation = async ({
    variables: {
      rebookData,
      originalData,
      reference,
      isExchangingMultipleLegsOfAOutboundSummated,
      isExchangingSinglegOfAOutboundSummated,
      isExchangingMultipleLegsOfAInboundSummated,
      isExchangingSingleLegOfAInboundSummated,
      isExchangingOutboundDirectFare,
      isExchangingInboundDirectFare,
      isExchangingOutboundThroughFare,
      isExchangingInboundThroughFare,
      isFreeExchange,
      exchangingOutboundSameClassOfService,
      exchangingInboundSameClassOfService
    }
  }) => {
    const outboundPendingCancellationLeg = rebookData?.pendingCancellationLegs?.filter((leg) => leg.direction === OUTBOUND);
    const outboundPendingCreationRebooked = rebookData?.pendingCreationLegs?.filter((leg) => leg.direction === OUTBOUND);
    const outboundPendingCreationLeg = originalData?.outboundSelectedFare;

    const inboundPendingCancellationLeg = rebookData?.pendingCancellationLegs?.filter((leg) => leg.direction === INBOUND);
    const inboundPendingCreationRebooked = rebookData?.pendingCreationLegs?.filter((leg) => leg.direction === INBOUND);
    const inboundPendingCreationLeg = originalData?.inboundSelectedFare;

    const isExchangingOutbound = outboundPendingCancellationLeg?.length > 0 && outboundPendingCreationRebooked?.length > 0;
    const isExchangingInbound = inboundPendingCancellationLeg?.length > 0 && inboundPendingCreationRebooked?.length > 0;

    if (isExchangingOutbound) {
      const isExchangingToDirectFare = isDirectFareLegs(outboundPendingCreationRebooked);
      const isExchangingToThroughFare = isThroughFareLegs(outboundPendingCreationRebooked);
      const isExchangingToSummatedFare = isSummatedFareLegs(outboundPendingCreationRebooked);

      const serviceTypes = outboundPendingCancellationLeg?.map(({ serviceType }) => serviceType?.code);
      const isSNSummatedFare =
        serviceTypes?.includes(serviceTypeCodes.SNCF) &&
        (serviceTypes?.includes(serviceTypeCodes.EUROSTAR) || serviceTypes?.includes(serviceTypeCodes.GREENSPEED));
      const isES = serviceTypes?.includes(serviceTypeCodes.EUROSTAR);

      // ES/ER Summated are treated diffrently than ES/SN or ER/SN summated fares
      const priceRequest = isSNSummatedFare
        ? overridePriceRequestSN({
            cancellationLeg: outboundPendingCancellationLeg,
            createdLeg: outboundPendingCreationLeg,
            createdLegRebooked: outboundPendingCreationRebooked,
            isExchangingSummatedFare: isExchangingMultipleLegsOfAOutboundSummated,
            isExchangingThroughFare: isExchangingOutboundThroughFare,
            isExchangingToSummatedFare: isExchangingToSummatedFare,
            carrier: isES ? serviceTypeCodes.EUROSTAR : serviceTypeCodes.GREENSPEED,
            shouldOverridePrice: isFreeExchange && exchangingOutboundSameClassOfService
          })
        : overridePriceRequest({
            cancellationLeg: outboundPendingCancellationLeg,
            createdLeg: outboundPendingCreationLeg,
            createdLegRebooked: outboundPendingCreationRebooked,
            isExchangingSummatedFare: isExchangingMultipleLegsOfAOutboundSummated,
            isExchangingDirectFare: isExchangingOutboundDirectFare,
            isExchangingThroughFare: isExchangingOutboundThroughFare,
            isExchangingToSummatedFare: isExchangingToSummatedFare,
            isExchangingToDirectFare: isExchangingToDirectFare,
            isExchangingToThroughFare: isExchangingToThroughFare
          });

      if (priceRequest.length > 0) {
        await overridePrice({
          variables: {
            reference,
            items: priceRequest
          }
        });
      }
    }

    if (isExchangingInbound) {
      const isExchangingToDirectFare = isDirectFareLegs(inboundPendingCreationRebooked);
      const isExchangingToThroughFare = isThroughFareLegs(inboundPendingCreationRebooked);
      const isExchangingToSummatedFare = isSummatedFareLegs(inboundPendingCreationRebooked);

      const serviceTypes = inboundPendingCancellationLeg?.map(({ serviceType }) => serviceType?.code);
      const isSNSummatedFare =
        serviceTypes?.includes(serviceTypeCodes.SNCF) &&
        (serviceTypes?.includes(serviceTypeCodes.EUROSTAR) || serviceTypes?.includes(serviceTypeCodes.GREENSPEED));
      const isES = serviceTypes?.includes(serviceTypeCodes.EUROSTAR);
      // ES/ER Summated are treated diffrently than ES/SN or ER/SN summated fares
      const priceRequest = isSNSummatedFare
        ? overridePriceRequestSN({
            cancellationLeg: inboundPendingCancellationLeg,
            createdLeg: inboundPendingCreationLeg,
            createdLegRebooked: inboundPendingCreationRebooked,
            isExchangingSummatedFare: isExchangingMultipleLegsOfAInboundSummated,
            isExchangingThroughFare: isExchangingInboundThroughFare,
            isExchangingToSummatedFare: isExchangingToSummatedFare,
            carrier: isES ? serviceTypeCodes.EUROSTAR : serviceTypeCodes.GREENSPEED,
            shouldOverridePrice: isFreeExchange && exchangingInboundSameClassOfService
          })
        : overridePriceRequest({
            cancellationLeg: inboundPendingCancellationLeg,
            createdLeg: inboundPendingCreationLeg,
            createdLegRebooked: inboundPendingCreationRebooked,
            isExchangingSummatedFare: isExchangingMultipleLegsOfAInboundSummated,
            isExchangingDirectFare: isExchangingInboundDirectFare,
            isExchangingThroughFare: isExchangingInboundThroughFare,
            isExchangingToSummatedFare: isExchangingToSummatedFare,
            isExchangingToDirectFare: isExchangingToDirectFare,
            isExchangingToThroughFare: isExchangingToThroughFare
          });

      if (priceRequest.length > 0) {
        await overridePrice({
          variables: {
            reference,
            items: priceRequest
          }
        });
      }
    }
  };

  return [
    handleMutation,
    {
      loading: overridePriceLoading
    }
  ];
};

export default useSummatedFaresOverrideMutation;
